import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  DeleteRounded,
  DriveFileRenameOutlineRounded,
  MoreVertRounded,
  TaskRounded,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import axios from "axios";
import dayjs from "dayjs";
import EmptyList from "../../../assets/empty-todos.webp";
import { useDialog } from "../../DialogContext";
import { backend_api } from "../../../static/server";
import { updateTaskOrType } from "../../../redux/Apps/Lawyerapp/TaskSlice/TaskSlice";
import CreateTaskForm from "../../../pages/LawyerPages/Task/TaskModuleForms/CreateTaskForm";
import {
  appendTeamMemberLawyerId,
  formatteDateDay,
  formattedDate,
  generateUniqueMatterName,
} from "../../../styles/styles";
import { DataGridNoData } from "../../../static/staticdata";
import DataTable from "../../DataTable";
const TaskTableForDash = ({
  // Required props
  tasks = [],
  loading = false,

  // Optional props with defaults
  height = "100%",
  minHeight = 0,
  emptyStateText = "No tasks found",
  emptyStateImage = EmptyList,

  // Pagination props
  pagination = null,
  onPageChange = () => {},

  // Column visibility control
  hiddenColumns = [],
  showColumns = null, // If provided, only show these columns

  // Customization
  showActionColumn = true,
  showFilterChips = false,
  filterChips = [],
  showToolbar = true,
  CustomToolbar = null,

  // Callbacks
  onTaskDelete = null,
  onTaskEdit = null,
  onTaskUpdate = null,
  onRefresh = () => {},

  // External dependencies
  token = "",
  teamMemberLawyerId = "",

  // Custom row actions
  customRowActions = null,

  // Action controls
  enableTaskDetails = true,
  enableEdit = true,
  enableDelete = true,

  // Styles
  sx = {},
  tableProps = {},
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showDialog } = useDialog();

  // State management
  const [actionMenu, setActionMenu] = useState({
    anchorEl: null,
    rowData: null,
  });

  const [taskDetails, setTaskDetails] = useState({
    anchorEl: null,
    rowData: null,
  });

  const formatActorName = useCallback((actor) => {
    if (!actor) return "-";

    const fullName = `${actor?.first_name || ""} ${actor?.last_name || ""}`.trim();
    return fullName || actor?.reminder_mail || "-";
  }, []);

  const taskDetailReminders = useMemo(() => {
    const alarms = taskDetails.rowData?.full_data?.task_alarm;
    if (!Array.isArray(alarms)) {
      return [];
    }

    return alarms.map((alarm, index) => {
      const rawValue = alarm?.alram_at || alarm?.date;
      const parsedValue = rawValue ? dayjs(rawValue) : null;
      const reminderType =
        alarm?.via === "notification"
          ? "In-app"
          : alarm?.via
            ? String(alarm.via).replace(/^./, (char) => char.toUpperCase())
            : "Reminder";

      return {
        id: `task-reminder-${index}-${rawValue || "na"}`,
        label: alarm?.exactness
          ? `${reminderType} (${alarm.exactness})`
          : reminderType,
        value:
          parsedValue && parsedValue.isValid()
            ? parsedValue.format("MMM D, YYYY hh:mm A")
            : rawValue || "Not scheduled",
      };
    });
  }, [taskDetails.rowData]);

  // Task operations
  const handleStatusChange = useCallback(async (id, newStatus) => {
    const statusMap = {
      Pending: "Open",
      "In Progress": "Transfered",
      "In Review": "Reviewed",
      "Not Need": "",
      Complete: "Completed",
    };

    try {
      const urlBase = `${backend_api}task/task-register/${id}/`;
      await dispatch(
        updateTaskOrType({
          url: urlBase,
          body: { status: statusMap[newStatus] },
          token,
          teamMemberLawyerId,
        }),
      ).unwrap();

      onRefresh();
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  }, [dispatch, onRefresh, teamMemberLawyerId, token]);

  const handleEditTask = useCallback((item) => {
    if (onTaskEdit) {
      onTaskEdit(item);
    } else {
      showDialog(
        <CreateTaskForm
          isEditMode={true}
          formData={item}
          editTaskId={item?.id}
        />,
        {
          dialogTitle: "Edit Task",
          onSuccess: onRefresh,
        },
      );
    }
  }, [onRefresh, onTaskEdit, showDialog]);

  const handleDeleteTask = useCallback(async (task) => {
    if (onTaskDelete) {
      onTaskDelete(task);
      return;
    }

    try {
      const deleteUrl = appendTeamMemberLawyerId(
        `${backend_api}task/task-register/${task.id}/`,
        teamMemberLawyerId,
      );
      const res = await axios.delete(
        deleteUrl,
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (res?.status === 204) {
        onRefresh();
        toast.success("Task deleted successfully");
      }
    } catch (error) {
      toast.error("Failed to delete the task. Please try again");
    }
  }, [onRefresh, onTaskDelete, teamMemberLawyerId, token]);

  const resolvedRowCount =
    pagination?.rowCount ?? pagination?.count ?? tasks.length;

  // All possible columns definition
  const allColumns = useMemo(
    () => [
      // Action Column
      {
        field: "action",
        headerName: "Actions",
        width: 80,
        sortable: false,
        disableColumnMenu: true,
        resizable: false,
        renderCell: (params) => {
          return (
            <Box
              sx={{
                display: "flex",
                justifyContent: "start",
                alignItems: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <Tooltip title="More" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActionMenu({
                      anchorEl: e.currentTarget,
                      rowData: params.row,
                    });
                  }}
                >
                  <MoreVertRounded sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={actionMenu.anchorEl}
                open={Boolean(actionMenu.anchorEl)}
                onClose={() => setActionMenu({ anchorEl: null, rowData: null })}
                elevation={2}
                slotProps={{
                  root: { sx: { ".MuiList-root": { padding: 0 } } },
                }}
              >
                {enableTaskDetails && (
                  <MenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setTaskDetails({
                        anchorEl: e.currentTarget,
                        rowData: params.row,
                      });
                    }}
                    sx={{
                      py: 1,
                      px: 2,
                      gap: 1.5,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <TaskRounded
                      sx={{
                        fontSize: 18,
                        color: "action.active",
                      }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Task Details
                      </Typography>
                    </Box>
                  </MenuItem>
                )}

                {enableEdit && (
                  <MenuItem
                    onClick={() => {
                      handleEditTask(actionMenu?.rowData?.full_data);
                      setActionMenu({ anchorEl: null, rowData: null });
                    }}
                    sx={{
                      py: 1,
                      px: 2,
                      gap: 1.5,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <DriveFileRenameOutlineRounded
                      sx={{
                        fontSize: 18,
                        color: "action.active",
                      }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Edit
                      </Typography>
                    </Box>
                  </MenuItem>
                )}

                {customRowActions &&
                  customRowActions(params.row, {
                    closeMenu: () =>
                      setActionMenu({ anchorEl: null, rowData: null }),
                  })}

                {enableDelete && (
                  <MenuItem
                    onClick={() => {
                      handleDeleteTask(actionMenu.rowData);
                      setActionMenu({ anchorEl: null, rowData: null });
                    }}
                    sx={{
                      borderTop: "1px solid #e5e5e5",
                      py: 1,
                      px: 2,
                      gap: 1.5,
                      color: "error.main",
                      "&:hover": {
                        bgcolor: "error.main",
                        color: "error.contrastText",
                        "& .MuiSvgIcon-root": {
                          color: "error.contrastText",
                        },
                      },
                    }}
                  >
                    <DeleteRounded
                      sx={{
                        fontSize: 18,
                        color: "error.main",
                      }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Delete
                      </Typography>
                    </Box>
                  </MenuItem>
                )}
              </Menu>
            </Box>
          );
        },
      },

      // Task Name Column
      {
        field: "task_name",
        headerName: "Task Name",
        flex: 3,
        minWidth: 220,
        maxWidth: 400,
        renderCell: (params) => (
          <Box
            sx={{
              width: "100%",
              py: 0.5,
            }}
          >
            <Tooltip title="Click to view task details" arrow>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  cursor: "pointer",
                  color: "text.primary",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.3,
                  "&:hover": {
                    color: "primary.main",
                    textDecoration: "underline",
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setTaskDetails({
                    anchorEl: e.currentTarget,
                    rowData: params.row,
                  });
                }}
              >
                {params.row?.task_name || "Untitled Task"}
              </Typography>
            </Tooltip>

            <Menu
              anchorEl={taskDetails.anchorEl}
              open={Boolean(taskDetails.anchorEl)}
              onClose={() => setTaskDetails({ anchorEl: null, rowData: null })}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              PaperProps={{
                sx: {
                  borderRadius: "0.75rem",
                  minWidth: "18rem",
                  maxWidth: "20rem",
                  border: "1px solid rgba(0,0,0,0.06)",
                },
              }}
            >
              <Box
                sx={{
                  p: "0.5rem",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  color: "white",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    color: "primary.main",
                  }}
                >
                  Task Details
                </Typography>
              </Box>

              <Box sx={{ p: "1rem" }}>
                {/* Task Name */}
                <Box sx={{ mb: "0.75rem" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontSize: "0.65rem",
                      fontWeight: 500,
                    }}
                  >
                    Task Name
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, mt: "0.25rem", fontSize: "0.8rem" }}
                  >
                    {taskDetails.rowData?.task_name || "Untitled Task"}
                  </Typography>
                </Box>

                {/* Dates Row */}
                <Box sx={{ display: "flex", gap: "0.75rem", mb: "0.75rem" }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        fontSize: "0.65rem",
                      }}
                    >
                      Estimate Date
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: "0.25rem", fontSize: "0.75rem" }}
                    >
                      {taskDetails.rowData?.estimate_time || "—"}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        fontSize: "0.65rem",
                      }}
                    >
                      Due Date
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: "0.25rem",
                        fontSize: "0.75rem",
                        color:
                          taskDetails.rowData?.due_date &&
                          new Date(taskDetails.rowData?.due_date) < new Date()
                            ? "error.main"
                            : "text.primary",
                      }}
                    >
                      {taskDetails.rowData?.due_date || "—"}
                    </Typography>
                  </Box>
                </Box>

                {/* Status */}
                <Box sx={{ mb: "0.75rem" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontSize: "0.65rem",
                    }}
                  >
                    Status
                  </Typography>
                  <Box sx={{ mt: "0.25rem" }}>
                    <Box
                      sx={{
                        display: "inline-block",
                        px: "0.5rem",
                        py: "0.25rem",
                        borderRadius: "0.5rem",
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: "white",
                        backgroundColor: (() => {
                          switch (taskDetails.rowData?.status) {
                            case "Pending":
                              return "#FFC107";
                            case "In Progress":
                              return "#2196F3";
                            case "In Review":
                              return "#FF9800";
                            case "Not Need":
                              return "#9E9E9E";
                            case "Complete":
                              return "#4CAF50";
                            default:
                              return "#9E9E9E";
                          }
                        })(),
                      }}
                    >
                      {taskDetails.rowData?.status || "Pending"}
                    </Box>
                  </Box>
                </Box>

                {/* Priority */}
                <Box sx={{ mb: "0.75rem" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontSize: "0.65rem",
                    }}
                  >
                    Priority
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: "0.25rem",
                      fontSize: "0.75rem",
                      color:
                        taskDetails.rowData?.priority === "high"
                          ? "error.main"
                          : taskDetails.rowData?.priority === "medium"
                            ? "warning.main"
                            : "success.main",
                      fontWeight: 500,
                    }}
                  >
                    {taskDetails.rowData?.priority || "—"}
                  </Typography>
                </Box>

                {/* Description */}
                {taskDetails.rowData?.description &&
                  taskDetails.rowData?.description !== "—" && (
                    <Box sx={{ mb: "0.75rem" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          fontSize: "0.65rem",
                        }}
                      >
                        Description
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: "0.25rem",
                          color: "text.secondary",
                          fontSize: "0.75rem",
                          lineHeight: 1.4,
                        }}
                      >
                        {taskDetails.rowData.description}
                      </Typography>
                    </Box>
                  )}

                {/* Matter */}
                {taskDetails.rowData?.case &&
                  taskDetails.rowData?.case !== "—" && (
                    <Box sx={{ mb: "0.75rem" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          fontSize: "0.65rem",
                        }}
                      >
                        Matter
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: "0.25rem",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        {taskDetails.rowData.case}
                      </Typography>
                    </Box>
                  )}

                {/* Assignment Info */}
                <Box sx={{ display: "flex", gap: "0.75rem" }}>
                  {taskDetails.rowData?.assigned_to &&
                    taskDetails.rowData?.assigned_to !== "—" && (
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            fontSize: "0.65rem",
                          }}
                        >
                          Assigned To
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ mt: "0.25rem", fontSize: "0.75rem" }}
                        >
                          {taskDetails.rowData.assigned_to}
                        </Typography>
                      </Box>
                    )}
                </Box>
                {taskDetails.rowData?.assigned_by &&
                  taskDetails.rowData?.assigned_by !== "â€”" && (
                    <Box sx={{ mt: "0.75rem" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          fontSize: "0.65rem",
                        }}
                      >
                        Assigned By
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: "0.25rem", fontSize: "0.75rem" }}
                      >
                        {taskDetails.rowData.assigned_by}
                      </Typography>
                    </Box>
                  )}
                {taskDetailReminders.length > 0 && (
                  <Box sx={{ mt: "0.75rem" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        fontSize: "0.65rem",
                      }}
                    >
                      Reminders
                    </Typography>
                    <Box sx={{ mt: "0.35rem", display: "grid", gap: "0.45rem" }}>
                      {taskDetailReminders.map((reminder) => (
                        <Box
                          key={reminder.id}
                          sx={{
                            px: "0.6rem",
                            py: "0.45rem",
                            borderRadius: "0.55rem",
                            bgcolor: "rgba(103, 80, 164, 0.06)",
                            border: "1px solid rgba(103, 80, 164, 0.12)",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              color: "text.secondary",
                              fontSize: "0.65rem",
                              textTransform: "uppercase",
                              letterSpacing: 0.4,
                            }}
                          >
                            {reminder.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ mt: "0.1rem", fontSize: "0.75rem" }}
                          >
                            {reminder.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Menu>
          </Box>
        ),
      },

      // Priority Column
      {
        field: "priority",
        headerName: "Priority",
        width: 130,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Box sx={{ width: "100%", textAlign: "center" }}>
            <Chip
              label={params.row?.priority || "—"}
              size="small"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                bgcolor:
                  params.row?.priority === "high"
                    ? "#FFEBEE"
                    : params.row?.priority === "medium"
                      ? "#FFF3E0"
                      : "#E8F5E9",
                color:
                  params.row?.priority === "high"
                    ? "#C62828"
                    : params.row?.priority === "medium"
                      ? "#EF6C00"
                      : "#2E7D32",
                border: "1px solid",
                borderColor:
                  params.row?.priority === "high"
                    ? "#FFCDD2"
                    : params.row?.priority === "medium"
                      ? "#FFE0B2"
                      : "#C8E6C9",
              }}
            />
          </Box>
        ),
      },

      // Status Column
      {
        field: "status",
        headerName: "Status",
        width: 140,
        renderCell: (params) => {
          const getStatusColor = (status) => {
            switch (status) {
              case "Pending":
                return "#FFC107";
              case "In Progress":
                return "#2196F3";
              case "In Review":
                return "#FF9800";
              case "Not Need":
                return "#9E9E9E";
              case "Complete":
                return "#4CAF50";
              default:
                return "#9E9E9E";
            }
          };

          const statusColor = getStatusColor(params?.row?.status);

          return (
            <Box sx={{ width: "100%" }}>
              <Select
                size="small"
                value={params?.row?.status || "Pending"}
                onChange={(e) => {
                  e.stopPropagation();
                  handleStatusChange(params?.row?.id, e.target.value);
                }}
                sx={{
                  height: 24,
                  fontSize: "0.72rem",
                  bgcolor: statusColor,
                  color: "#fff",
                  borderRadius: "12px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "& .MuiSelect-select": {
                    py: 0,
                    px: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#fff",
                    fontSize: "16px",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: "8px",
                      boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
                      "& .MuiMenuItem-root": {
                        fontSize: "0.75rem",
                        minHeight: 28,
                      },
                    },
                  },
                }}
              >
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="In Review">In Review</MenuItem>
                <MenuItem value="Not Need">Not Need</MenuItem>
                <MenuItem value="Complete">Complete</MenuItem>
              </Select>
            </Box>
          );
        },
      },

      // Description Column
      {
        field: "description",
        headerName: "Description",
        flex: 2,
        minWidth: 180,
        maxWidth: 300,
        renderCell: (params) => (
          <Tooltip
            title={params?.row?.description || "No description"}
            arrow
            placement="top-start"
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.85rem",
                color: "text.secondary",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {params?.row?.description || "No description"}
            </Typography>
          </Tooltip>
        ),
      },

      // Relates To Column
      {
        field: "relates_to",
        headerName: "Relates To",
        flex: 2,
        minWidth: 200,
        maxWidth: 350,
        sortable: false,
        renderCell: (params) => {
          const matter =
            params.row.case && params.row.case !== "-" ? params.row.case : null;
          const client = params.row.client;

          if (!matter && !client) {
            return (
              <Typography variant="body2" sx={{ color: "text.disabled" }}>
                —
              </Typography>
            );
          }

          return (
            <Box sx={{ width: "100%", py: 0.5 }}>
              {matter && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    color: "text.primary",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    mb: 0.5,
                    "&:hover": {
                      cursor: "pointer",
                      textDecoration: "underline",
                    },
                  }}
                  title={matter}
                  onClick={() => {
                    if (params.row.case_id) {
                      navigate(`/dashboard/matter/${params.row.case_id}/`);
                    }
                  }}
                >
                  Matter: {matter}
                </Typography>
              )}

              {!matter && client && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.85rem",
                    color: "text.primary",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      cursor: "pointer",
                      textDecoration: "underline",
                    },
                  }}
                  onClick={() => {
                    if (client?.id) {
                      if (client?.contact_type === "person") {
                        navigate(`/dashboard/people/${client?.id}`);
                      } else if (client?.contact_type === "company") {
                        navigate(`/dashboard/companies/${client?.id}`);
                      }
                    }
                  }}
                >
                  {client?.contact_type === "person" ? "Person" : "Company"}:
                  {client?.contact_type === "person"
                    ? ` ${client?.first_name || ""} ${
                        client?.last_name || ""
                      }`.trim()
                    : ` ${client?.company_name || "-"}`}
                </Typography>
              )}
            </Box>
          );
        },
      },

      // Matter Ref ID Column
      {
        field: "matter_ref_id",
        headerName: "Matter-Ref ID",
        width: 200,
        sortable: false,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.85rem",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
              "&:hover": params.row.case_id
                ? {
                    cursor: "pointer",
                    textDecoration: "underline",
                  }
                : {},
            }}
            onClick={() => {
              if (params.row.case_id) {
                navigate(`/dashboard/matter/${params.row.case_id}/`);
              }
            }}
          >
            {params?.row?.matter_ref_id || "—"}
          </Typography>
        ),
      },

      // Assigned To Column
      {
        field: "assigned_to",
        headerName: "Assigned To",
        width: 200,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.85rem",
              color: "text.primary",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {params?.row?.assigned_to || "Unassigned"}
          </Typography>
        ),
      },

      // Assigned By Column
      {
        field: "assigned_by",
        headerName: "Assigned By",
        width: 150,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.85rem",
              color: "text.secondary",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {params?.row?.assigned_by || "—"}
          </Typography>
        ),
      },

      // Estimate Time Column
      {
        field: "estimate_time",
        headerName: "Est. Time",
        width: 200,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.85rem",
              color: "text.primary",
              fontWeight: 500,
            }}
          >
            {params?.row?.estimate_time || "—"}
          </Typography>
        ),
      },

      // Due Date Column
      {
        field: "due_date",
        headerName: "Due Date",
        width: 130,
        renderCell: (params) => {
          const dueDate = params?.row?.due_date;
          const isOverdue = dueDate && new Date(dueDate) < new Date();

          return (
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.85rem",
                color: isOverdue ? "error.main" : "text.primary",
                fontWeight: isOverdue ? 600 : 400,
              }}
            >
              {dueDate || "—"}
            </Typography>
          );
        },
      },

      // Created Date Column
      {
        field: "created_date",
        headerName: "Created Date",
        width: 130,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.85rem",
            }}
          >
            {params.row.created_date || "—"}
          </Typography>
        ),
      },

      // Client Column (hidden by default, can be shown if needed)
      {
        field: "client",
        headerName: "Client",
        width: 200,
        renderCell: (params) => {
          const client = params.row.client;
          if (!client) return "—";

          return (
            <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
              {client.contact_type === "person"
                ? `${client.first_name || ""} ${client.last_name || ""}`.trim()
                : client.company_name || "—"}
            </Typography>
          );
        },
      },

      // Private Task Column (hidden by default)
      {
        field: "private_task",
        headerName: "Private",
        width: 80,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Chip
            label={params.row.private_task ? "Yes" : "No"}
            size="small"
            color={params.row.private_task ? "warning" : "default"}
            variant="outlined"
            sx={{ fontSize: "0.7rem" }}
          />
        ),
      },
    ],
    [
      actionMenu,
      handleDeleteTask,
      handleEditTask,
      handleStatusChange,
      taskDetails,
      taskDetailReminders,
      navigate,
      customRowActions,
      enableTaskDetails,
      enableEdit,
      enableDelete,
    ],
  );

  // Filter columns based on props
  const filteredColumns = useMemo(() => {
    let columns = allColumns;

    // If showColumns is provided, only include those columns
    if (showColumns && Array.isArray(showColumns)) {
      columns = columns.filter((col) => showColumns.includes(col.field));
    }

    // Remove hidden columns
    if (hiddenColumns && Array.isArray(hiddenColumns)) {
      columns = columns.filter((col) => !hiddenColumns.includes(col.field));
    }

    // Remove action column if disabled
    if (!showActionColumn) {
      columns = columns.filter((col) => col.field !== "action");
    }

    return columns;
  }, [allColumns, showColumns, hiddenColumns, showActionColumn]);

  // Transform tasks data to rows
  const rows = useMemo(() => {
    if (!Array.isArray(tasks)) {
      console.error("tasks is not an array:", tasks);
      return [];
    }

    return tasks.map((item) => ({
      id: item?.id,
      client_id: item?.client?.id,
      task_name: item?.task_name ?? "-",
      description: item?.comment ?? "-",
      priority: item?.priority === "0" ? "Not Set" : item?.priority || "-",
      status:
        item?.status === "Open"
          ? "Pending"
          : item?.status === "Transfered"
            ? "In Progress"
            : item?.status === "Reviewed"
              ? "In Review"
              : item?.status === "Completed"
                ? "Complete"
                : item?.status === ""
                  ? "Not Need"
                  : "-",
      case: item?.case
        ? generateUniqueMatterName(item?.case?.id, item?.case?.case_name)
        : "-",
      case_id: item?.case?.id,
      matter_ref_id: item?.case?.client_ref,
      client: item?.client,
      assigned_to: formatActorName(
        item?.assign_to || item?.created_by_emp || item?.created_by,
      ),
      assigned_by: formatActorName(item?.created_by_emp || item?.created_by),
      estimate_time: item?.estimate_time
        ? formattedDate(item?.estimate_time)
        : "-",
      due_date: item?.closed_on ? formatteDateDay(item?.closed_on) : "-",
      private_task: item?.private_task || false,
      full_data: item,
      created_date: item?.created_date
        ? formatteDateDay(item?.created_date)
        : "-",
    }));
  }, [formatActorName, tasks]);

  const CustomNoRowsOverlay = useCallback(
    () => <DataGridNoData imgSrc={emptyStateImage} text={emptyStateText} />,
    [emptyStateImage, emptyStateText],
  );

  return (
    <Box
      sx={{
        width: "100%",
        height,
        minHeight,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      <DataTable
        columns={filteredColumns}
        rows={rows}
        CustomNoRowsOverlay={CustomNoRowsOverlay}
        height="100%"
        minHeight={0}
        loading={loading}
        page={pagination?.page || 1}
        setPage={(page) => onPageChange(page)}
        pageSize={pagination?.pageSize || 10}
        rowCount={resolvedRowCount}
        count={resolvedRowCount}
        hasNextPage={pagination?.hasNextPage || false}
        hasPreviousPage={pagination?.hasPreviousPage || false}
        filterChips={showFilterChips ? filterChips : []}
        CustomToolbar={CustomToolbar}
        showToolbar={showToolbar}
        {...tableProps}
      />
    </Box>
  );
};

export default React.memo(TaskTableForDash);
